import React, { useCallback, useRef } from 'react';

const KM_TICKS = [3, 5, 10, 15, 20, 25, 30, 40, 50];
const KM_MIN = 3;
const KM_MAX = 50;

function clampKm(value) {
  const n = parseInt(String(value == null ? '' : value).replace(/\D/g, ''), 10);
  if (!Number.isFinite(n)) return 5;
  return Math.min(KM_MAX, Math.max(KM_MIN, n));
}

function scaleToPosition(value, min, max) {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * 100;
}

function valueFromRatio(ratio) {
  const clamped = Math.max(0, Math.min(1, ratio));
  return clampKm(Math.round(KM_MIN + clamped * (KM_MAX - KM_MIN)));
}

export default function OpenOsV2KmSlider({ value, onChange }) {
  const km = clampKm(value);
  const trackRef = useRef(null);
  const thumbLeft = scaleToPosition(km, KM_MIN, KM_MAX);

  const pickFromClientX = useCallback(
    (clientX) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      onChange(valueFromRatio((clientX - rect.left) / rect.width));
    },
    [onChange]
  );

  const handlePointerDown = (event) => {
    event.preventDefault();
    pickFromClientX(event.clientX);
    const onMove = (moveEvent) => pickFromClientX(moveEvent.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(Math.min(KM_MAX, km + 1));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(Math.max(KM_MIN, km - 1));
    }
  };

  return (
    <div className="oosv2-agenda-km">
      <div className="oosv2-agenda-km-control">
        <div className="oosv2-agenda-km-rail">
          <div
            ref={trackRef}
            className="oosv2-agenda-km-track"
            role="slider"
            aria-valuemin={KM_MIN}
            aria-valuemax={KM_MAX}
            aria-valuenow={km}
            aria-label="Raio em quilômetros"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          >
            <div className="oosv2-agenda-km-line" aria-hidden="true" />
            <div className="oosv2-agenda-km-fill" style={{ width: `${thumbLeft}%` }} aria-hidden="true" />
            <div className="oosv2-agenda-km-thumb" style={{ left: `${thumbLeft}%` }} aria-hidden="true" />
          </div>
          <div className="oosv2-agenda-km-ticks" aria-hidden="true">
            {KM_TICKS.map((tick) => {
              const left = scaleToPosition(tick, KM_MIN, KM_MAX);
              const edgeClass = tick === KM_MIN
                ? ' oosv2-agenda-km-tick--start'
                : tick === KM_MAX
                  ? ' oosv2-agenda-km-tick--end'
                  : '';
              return (
                <span
                  key={tick}
                  className={`oosv2-agenda-km-tick${edgeClass}`}
                  style={{ left: `${left}%` }}
                >
                  {tick}
                </span>
              );
            })}
          </div>
        </div>
        <span className="oosv2-agenda-km-value">{km} km</span>
      </div>
    </div>
  );
}

export { clampKm };
